/**
 * Plain text from HTML by pattern, the logic behind the HTML Tag Remover. It is a
 * text tidier, not an HTML parser: it removes real tags (a `<` must be followed by a
 * letter, `/`, `!` or `?`, so "5 < 6 and 7 > 3" survives), keeps block, row and cell
 * boundaries as line breaks and tabs, and decodes each character reference once.
 */
import { decodeReference } from "@/lib/tool-kit/developer/markdown/text";

const RE_HTML_COMMENTS = /<!--[\s\S]*?-->/g;
const RE_STYLE_TAGS = /<style\b[^>]*>[\s\S]*?<\/style\s*>/gi;
const RE_SCRIPT_TAGS = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;
// A tag name, then attributes in which a quoted value may hold `<` or `>`.
const TAG_BODY = "(?:\"[^\"]*\"|'[^']*'|[^<>\"'])*";
const RE_ALL_TAGS = new RegExp(
  `<\\/?[A-Za-z][A-Za-z0-9:-]*${TAG_BODY}>|<[!?][^>]*>`,
  "g"
);
const RE_BR_TAGS = /<br\b[^>]*>/gi;
const RE_BLOCK_END = /<\/(?:p|div|h[1-6]|li|tr)\s*>|<hr\b[^>]*>/gi;
const RE_CELL_END = /<\/t[dh]\s*>/gi;
// Source whitespace between table tags is layout, not a line break between cells.
const RE_TABLE_GAP =
  /(<\/?(?:table|thead|tbody|tfoot|tr|td|th)\b[^>]*>)\s+(?=<)/gi;
const RE_REFERENCE = /&(?:#[xX][0-9a-fA-F]+|#[0-9]+|[A-Za-z][A-Za-z0-9]+);/g;
const RE_NBSP = / /g;
const RE_MULTIPLE_NEWLINES = /\n{3,}/g;
const RE_MULTIPLE_SPACES = / {2,}/g;
const RE_TRAILING_TABS = /\t+(?=\n|$)/g;

/** Decode every character reference exactly once; unknown ones stay as typed. */
export function decodeEntities(text: string): string {
  return text
    .replace(RE_REFERENCE, (match) => decodeReference(match) ?? match)
    .replace(RE_NBSP, " ");
}

export interface StripResult {
  output: string;
  inputChars: number;
  outputChars: number;
  tagsRemoved: number;
}

export function stripHtml(html: string): StripResult | null {
  const trimmed = html.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const inputChars = trimmed.length;
  const tagsRemoved = (trimmed.match(RE_ALL_TAGS) ?? []).length;

  const result = decodeEntities(
    trimmed
      .replace(RE_HTML_COMMENTS, "")
      .replace(RE_SCRIPT_TAGS, "")
      .replace(RE_STYLE_TAGS, "")
      .replace(RE_TABLE_GAP, "$1")
      .replace(RE_BR_TAGS, "\n")
      .replace(RE_BLOCK_END, "\n")
      .replace(RE_CELL_END, "\t")
      .replace(RE_ALL_TAGS, "")
  )
    .replace(RE_TRAILING_TABS, "")
    .replace(RE_MULTIPLE_SPACES, " ")
    .replace(RE_MULTIPLE_NEWLINES, "\n\n")
    .trim();

  return {
    output: result,
    inputChars,
    outputChars: result.length,
    tagsRemoved,
  };
}
